// @borradh-workspace/api-client - Error utilities
/**
 * Custom error class for API errors
 * Extends Error with additional API-specific information
 */
export class ApiClientError extends Error {
    /** HTTP status code */
    status;
    /** Error code from the API */
    code;
    /** Additional error details */
    details;
    constructor(error) {
        super(error.message);
        this.name = 'ApiClientError';
        this.status = error.status;
        this.code = error.code;
        this.details = error.details;
        // Maintains proper stack trace (V8 engines)
        if (Error.captureStackTrace) {
            Error.captureStackTrace(this, ApiClientError);
        }
    }
    /**
     * Convert to plain object for logging/serialization
     */
    toJSON() {
        return {
            status: this.status,
            message: this.message,
            code: this.code,
            details: this.details,
        };
    }
}
/**
 * Check if an error is an ApiClientError
 */
export function isApiClientError(error) {
    return error instanceof ApiClientError;
}
/**
 * Error thrown by `apiClient` methods when the API returns a non-2xx response.
 * Carries the HTTP status, the request method/path, and the `requestId` echoed
 * by the API so browser errors can be correlated with server-side logs.
 */
export class ApiFetchError extends Error {
    status;
    method;
    path;
    requestId;
    code;
    details;
    constructor(opts) {
        super(opts.message);
        this.name = 'ApiFetchError';
        this.status = opts.status;
        this.method = opts.method;
        this.path = opts.path;
        this.requestId = opts.requestId;
        this.code = opts.code;
        this.details = opts.details;
        if (Error.captureStackTrace) {
            Error.captureStackTrace(this, ApiFetchError);
        }
    }
}
export function isApiFetchError(error) {
    return error instanceof ApiFetchError;
}
/**
 * Check if error is a network error (no response)
 */
export function isNetworkError(error) {
    return error instanceof TypeError && error.message === 'Failed to fetch';
}
/**
 * Check if error is a timeout error
 */
export function isTimeoutError(error) {
    if (error instanceof Error) {
        return error.name === 'TimeoutError' || error.message.includes('timeout');
    }
    return false;
}
/**
 * Extract Meta error details from any error thrown by the API client.
 * Returns undefined if the error doesn't contain Meta error info.
 *
 * Works with ky's HTTPError (which has `.code` and `.details` attached
 * by the beforeError hook) and ApiClientError instances.
 */
export function getMetaErrorDetail(error) {
    if (!error || typeof error !== 'object')
        return undefined;
    // Check for details.metaError on either error type
    const details = error.details;
    if (details?.metaError && typeof details.metaError === 'object') {
        return details.metaError;
    }
    return undefined;
}
/**
 * Async version of getMetaErrorDetail that also reads the response body
 * from ky HTTPError objects. Use this when the synchronous version returns
 * undefined — the error's `.details` property may not have been attached
 * if the beforeError hook's body read was skipped or failed.
 */
export async function getMetaErrorDetailAsync(error) {
    // Try the synchronous check first
    const sync = getMetaErrorDetail(error);
    if (sync)
        return sync;
    // Fallback: read the response body directly from ky's HTTPError
    if (error &&
        typeof error === 'object' &&
        'response' in error &&
        error.response instanceof Response) {
        try {
            const response = error.response;
            const body = (await response.clone().json());
            const bodyDetails = body.details;
            if (bodyDetails?.metaError && typeof bodyDetails.metaError === 'object') {
                return bodyDetails.metaError;
            }
        }
        catch {
            // Response not JSON or already consumed
        }
    }
    return undefined;
}
//# sourceMappingURL=errors.js.map